/**
 * "Arte do convite", the picture guests see.
 *
 * Two ways in: she uploads an image, or she describes one and the app draws it.
 * Both end in the same place, a resized pair of images she then saves, so a
 * generated picture she does not like costs nothing and leaves nothing behind.
 *
 * The same image, cropped differently, becomes the WhatsApp preview card, so a
 * shared link shows the invitation rather than a bare URL.
 */

import { useEffect, useState } from 'react';
import { api, ApiError, host, type PublicEvent } from '../api.js';
import { prepareArt, uploadArt, type PreparedArt } from './art-resize.js';

interface PresignResponse {
  uploads: { kind: string; url: string; maxBytes: number }[];
}

const PROMPT_MAX = 400;

export function Art({
  eventId,
  event,
  canGenerate,
  onChanged,
}: {
  eventId: string;
  event: PublicEvent | null;
  canGenerate: boolean;
  onChanged: (artUrl: string) => void;
}) {
  const [prepared, setPrepared] = useState<PreparedArt | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // A blob URL survives until it is revoked, and leaks the whole image if not.
  useEffect(() => {
    return () => {
      if (prepared) URL.revokeObjectURL(prepared.previewUrl);
    };
  }, [prepared]);

  async function accept(image: Blob) {
    setError(null);
    setSaved(false);
    setBusy(true);
    try {
      setPrepared(await prepareArt(image));
    } catch (err) {
      setError((err as Error).message);
      setPrepared(null);
    } finally {
      setBusy(false);
    }
  }

  async function save() {
    if (!prepared) return;
    setBusy(true);
    setError(null);
    try {
      const { uploads } = await api.post<PresignResponse>(
        `/api/events/${eventId}/arte`,
        { invite: prepared.invite.size, og: prepared.og.size },
      );
      await uploadArt(uploads, prepared);

      const { inviteUrl } = await api.post<{ inviteUrl: string }>(
        `/api/events/${eventId}/arte/confirmar`,
      );

      onChanged(inviteUrl);
      setSaved(true);
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : (err as Error).message,
      );
    } finally {
      setBusy(false);
    }
  }

  const current = prepared?.previewUrl ?? event?.artUrl ?? null;

  return (
    <div className="card">
      <strong>Arte do convite</strong>
      <p className="muted" style={{ marginTop: '0.35rem' }}>
        Aparece no convite e na prévia do link no WhatsApp. Qualquer foto ou
        imagem serve. A gente ajusta o tamanho.
      </p>

      {error && <div className="banner error">{error}</div>}
      {saved && <div className="banner ok">Arte salva.</div>}

      {current && (
        <img
          src={current}
          alt="Arte do convite"
          className="art-preview"
          loading="lazy"
        />
      )}

      {canGenerate && (
        <GenerateArt
          eventId={eventId}
          busy={busy}
          onDrawn={(image) => void accept(image)}
        />
      )}

      <label htmlFor="art-file">
        {canGenerate ? 'Ou escolher uma imagem sua' : 'Escolher imagem'}
      </label>
      <input
        id="art-file"
        type="file"
        accept="image/*"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void accept(file);
        }}
      />

      {prepared && (
        <button
          className="primary"
          style={{ marginTop: '0.75rem' }}
          onClick={save}
          disabled={busy}
        >
          {busy ? 'Enviando…' : 'Salvar arte'}
        </button>
      )}

      {busy && !prepared && <p className="muted">Preparando a imagem…</p>}
    </div>
  );
}

/**
 * Describe it and the app draws it.
 *
 * There is no photo to upload here on purpose. A reference photo of a child
 * produces a picture that looks like that child; a description of a theme
 * cannot. So this asks for a theme, and says so.
 */
function GenerateArt({
  eventId,
  busy,
  onDrawn,
}: {
  eventId: string;
  busy: boolean;
  onDrawn: (image: Blob) => void;
}) {
  const [prompt, setPrompt] = useState('');
  const [drawing, setDrawing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function draw() {
    setDrawing(true);
    setError(null);
    try {
      const { image } = await host.generateArt(eventId, prompt);
      onDrawn(blobFromBase64(image));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Não deu certo.');
    } finally {
      setDrawing(false);
    }
  }

  return (
    <div className="art-generate">
      <label htmlFor="art-prompt">Descreva a arte</label>
      <textarea
        id="art-prompt"
        rows={2}
        maxLength={PROMPT_MAX}
        value={prompt}
        disabled={drawing}
        onChange={(e) => setPrompt(e.target.value)}
        placeholder="Ex.: tema tigrinho, tons de laranja e verde, estilo aquarela"
      />
      <p className="muted" style={{ marginTop: '0.35rem' }}>
        Funciona melhor com tema, cores e estilo. Não desenha pessoas de
        verdade.
      </p>

      {error && <div className="banner error">{error}</div>}

      <button
        className="block"
        onClick={draw}
        disabled={drawing || busy || prompt.trim().length < 3}
      >
        {drawing ? 'Desenhando… leva uns 20 segundos' : 'Gerar arte'}
      </button>
    </div>
  );
}

/** Base64 from the API into something the resizer can open. */
function blobFromBase64(base64: string): Blob {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: 'image/png' });
}
